import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { HexAlphaColorPicker, HexColorInput } from 'react-colorful';
import { RiCloseLine, RiArrowDownSLine, RiPaletteLine } from '@remixicon/react';

const RECOMMENDED_COLORS = [
  '#9CA3AF',
  '#EF4444',
  '#F97316',
  '#EAB308',
  '#22C55E',
  '#60A5FA',
  '#3B82F6',
  '#8B5CF6',
  '#A855F7',
];

const hexAlphaToHex = (value) => {
  if (!value) return { hex: '', alpha: 100 };
  const v = value.replace('#', '');
  if (v.length === 8) {
    return { hex: v.slice(0, 6).toUpperCase(), alpha: Math.round((parseInt(v.slice(6), 16) / 255) * 100) };
  }
  return { hex: v.toUpperCase(), alpha: 100 };
};

const AddCustomColorModal = ({ open, onClose, onAdd, initialColor = '#B2E67B' }) => {
  const [color, setColor] = useState(initialColor);

  useEffect(() => {
    if (open) setColor(initialColor || '#B2E67B');
  }, [open, initialColor]);

  if (!open) return null;

  const { hex, alpha } = hexAlphaToHex(color);

  const handleAdd = () => {
    onAdd?.(color);
    onClose();
  };

  return createPortal(
    <>
      <div className="fixed inset-0 z-[10000] bg-black/30" onClick={onClose} />
      <div className="fixed z-[10001] top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[calc(100%-2rem)] max-w-md bg-white rounded-3xl shadow-xl overflow-hidden">
        <div className="flex items-center justify-between px-6 pt-5 pb-3">
          <h3 className="text-lg font-semibold text-primary-700">Color picker</h3>
          <button
            type="button"
            onClick={onClose}
            className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
            aria-label="Close"
          >
            <RiCloseLine className="w-5 h-5" />
          </button>
        </div>

        <div className="px-6 pb-6">
          <div className="rcp-wrapper">
            <HexAlphaColorPicker color={color} onChange={setColor} />
          </div>

          <div className="mt-4 flex items-center gap-3">
            <button
              type="button"
              className="flex items-center gap-1 px-2.5 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 rounded-lg"
            >
              Hex
              <RiArrowDownSLine className="w-4 h-4 text-gray-400" />
            </button>
            <div className="flex-1 relative">
              <HexColorInput
                color={hex}
                onChange={(v) => {
                  // react-colorful's HexColorInput always fires onChange
                  // with a `#`-prefixed value (its internal `process` is
                  // `"#" + e`, regardless of `prefixed`). Adding another
                  // `#` here yielded `##RRGGBB`, which the alpha picker
                  // and swatch comparisons then couldn't parse.
                  const withHash = v.startsWith('#') ? v : `#${v}`;
                  const alphaSuffix = alpha < 100
                    ? Math.round((alpha / 100) * 255).toString(16).padStart(2, '0')
                    : '';
                  setColor(`${withHash}${alphaSuffix}`);
                }}
                prefixed={false}
                className="w-full pl-3 pr-14 py-2 border border-gray-200 rounded-lg text-sm uppercase tracking-wide focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-gray-500">
                {alpha}%
              </span>
            </div>
          </div>

          <div className="mt-5">
            <p className="text-sm font-medium text-gray-700 mb-2.5">Recommended Colors</p>
            <div className="flex flex-wrap gap-2">
              {RECOMMENDED_COLORS.map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => setColor(c)}
                  className="w-7 h-7 rounded-full border border-transparent hover:scale-110 transition-transform"
                  style={{ backgroundColor: c }}
                  aria-label={`Select color ${c}`}
                />
              ))}
            </div>
          </div>

          <button
            type="button"
            onClick={handleAdd}
            className="mt-6 w-full flex items-center justify-center gap-2 py-3 rounded-full text-sm font-medium bg-primary-500 text-white hover:bg-primary-700 transition-colors"
          >
            <RiPaletteLine className="w-4 h-4" />
            Add custom color
          </button>
        </div>
      </div>

      <style>{`
        .rcp-wrapper .react-colorful { width: 100%; height: 200px; }
        .rcp-wrapper .react-colorful__saturation { border-radius: 12px; margin-bottom: 12px; }
        .rcp-wrapper .react-colorful__hue,
        .rcp-wrapper .react-colorful__alpha { border-radius: 999px; height: 12px; margin-bottom: 8px; }
        .rcp-wrapper .react-colorful__hue-pointer,
        .rcp-wrapper .react-colorful__alpha-pointer,
        .rcp-wrapper .react-colorful__saturation-pointer { width: 18px; height: 18px; }
      `}</style>
    </>,
    document.body
  );
};

export default AddCustomColorModal;
